import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain, Sparkles, TrendingUp, CheckCircle2, GraduationCap, Activity } from "lucide-react";
import { API } from "../lib/api";
import { PageHeader, Spinner } from "../components/common";
import NexusMotion from "../components/NexusMotion";

export default function AIIntelligence() {
  const [briefing, setBriefing] = useState(null);
  const [insights, setInsights] = useState(null);
  const [learning, setLearning] = useState(null);

  useEffect(() => {
    API.get("/ai/daily-briefing").then((r) => setBriefing(r.data));
    API.get("/ai/insights").then((r) => setInsights(r.data.insights));
    API.get("/ai/learning").then((r) => setLearning(r.data));
  }, []);

  const approve = async (idx) => { toast.success("AI recommendation approved & activated"); };

  if (!briefing || !insights || !learning) return <Spinner />;

  return (
    <div>
      <PageHeader title="AI Intelligence" subtitle="MB Nexus Intelligence — your AI sales manager." testid="ai-intelligence-header" />
      <div className="mb-card mb-5 flex items-center gap-3 overflow-hidden border-cyan-500/20 px-4 py-2"><NexusMotion variant="brain" size="normal"/><div><div className="caption-mono">NEXUS NEURAL LAYER</div><p className="text-xs text-slate-400">Briefings, learning signals, and sales insights.</p></div></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="mb-card p-5 border-purple-500/20" data-testid="briefing-panel">
          <div className="flex items-center gap-2 mb-4"><Brain className="h-5 w-5 text-purple-400" /><span className="font-heading font-semibold text-slate-100">Daily Briefing</span></div>
          <div className="text-sm text-slate-300 whitespace-pre-line leading-relaxed">{briefing.briefing}</div>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {Object.entries(briefing.stats).map(([k, v]) => (
              <div key={k} className="bg-slate-900/50 border border-slate-800 rounded-lg p-2 text-center">
                <div className="font-heading text-lg font-bold text-slate-100">{v}</div>
                <div className="text-[9px] text-slate-500 uppercase">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-card p-5" data-testid="insights-panel">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-amber-400" /><span className="font-heading font-semibold text-slate-100">Sales Insights</span></div>
          <div className="space-y-3">
            {insights.map((ins, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-800">
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25 mt-0.5">{ins.confidence}%</span>
                <span className="text-sm text-slate-300 flex-1">{ins.insight}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-card p-5" data-testid="learning-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-emerald-400" /><span className="font-heading font-semibold text-slate-100">AI Learning System</span></div>
            <div className="text-xs text-slate-400">Quality Score <span className="font-heading text-lg font-bold text-emerald-400 ml-1">{learning.quality_score}</span></div>
          </div>
          <div className="flex gap-3 mb-4">
            <div className="flex-1 bg-emerald-500/10 border border-emerald-500/25 rounded-lg p-3 text-center"><div className="font-heading text-xl font-bold text-emerald-400">{learning.won}</div><div className="text-[10px] text-slate-500">Won</div></div>
            <div className="flex-1 bg-slate-600/10 border border-slate-600/30 rounded-lg p-3 text-center"><div className="font-heading text-xl font-bold text-slate-400">{learning.lost}</div><div className="text-[10px] text-slate-500">Lost</div></div>
          </div>
          <div className="caption-mono mb-2">Controlled Recommendations (require approval)</div>
          {learning.recommendations.map((r, i) => (
            <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-slate-900/50 border border-slate-800 mb-2">
              <span className="text-sm text-slate-300 flex-1">{r.text}</span>
              <button onClick={() => approve(i)} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 flex-shrink-0" data-testid={`approve-rec-${i}`}><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>
            </div>
          ))}
        </div>

        <div className="mb-card p-5" data-testid="learning-events-panel">
          <div className="flex items-center gap-2 mb-4"><Activity className="h-5 w-5 text-blue-400" /><span className="font-heading font-semibold text-slate-100">Learning Events</span></div>
          <div className="space-y-2">
            {learning.events.length === 0 && <div className="text-sm text-slate-500">No closed deals yet to learn from.</div>}
            {learning.events.map((e) => (
              <div key={e.id} className="text-sm text-slate-300 bg-slate-900/40 border border-slate-800 rounded-lg p-2.5">{e.text}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
